import useSocketReconnect from '../../utils/useSocketReconnect';
import { useEffect, useState } from "react";
import { NoSignal } from "../Interface";
import { applyTimeZone } from "../../utils/utils";
import moment from "moment";
import CalendarsEditor from "./CalendarsEditor";
import EventCategoriesEditor from "./EventCategoriesEditor";
import Pressable from "../other/Pressable";
import UserInfo from "../login/UserInfo";
import HeaderTabs from "../interface/HeaderTabs";
import ShareCalendar from "./ShareCalendar";
import DeletedEvents from "./DeletedEvents";
import CalendarEventsInfiniteTable from "./CalendarEventsInfiniteTable";
import "../gestio/GestioApp.css";
import "./CalendarApp.css";

function CalendarApp(props) {
    useSocketReconnect(props.socket, () => {
        props.socket.emit('.request_calendar');
    });

    const { socket } = props;

    const [calendarEvents, setCalendarEvents] = useState({ 'calendar_ids': null, 'events': [] });

    const [eventsToBeDeleted, setEventsToBeDeleted] = useState([])
    const [calendarError, setCalendarError] = useState(null)

    const [activeTab, setActiveTab] = useState('calendaris');

    const calendarTabs = [
        { id: 'calendaris', label: 'Calendaris', icon: 'fa-calendar' },
        { id: 'afegir', label: 'Afegir', icon: 'fa-plus' },
        { id: 'compartir', label: 'Compartir', icon: 'fa-share-alt' },
        { id: 'esdeveniments', label: 'Esdeveniments', icon: 'fa-list' },
        { id: 'categories', label: 'Categories', icon: 'fa-tags' },
        { id: 'esborrats', label: 'Esborrats', icon: 'fa-trash' },
    ];

    const getTabFromPath = () => {
        const path = window.location.pathname;
        const parts = path.split('/');
        const lastPart = parts[parts.length - 1];

        if (calendarTabs.some(tab => tab.id === lastPart)) {
            return lastPart;
        }

        return 'calendaris';
    };

    const changeTab = (tab) => {
        setActiveTab(tab);

        const currentPath = window.location.pathname;
        const basePath = currentPath.replace(/\/(calendaris|afegir|compartir|esdeveniments|categories|esborrats)$/, '');
        const normalizedBasePath = basePath.endsWith('/') ? basePath : `${basePath}/`;

        window.history.pushState({ tab }, '', `${normalizedBasePath}${tab}`);
    };

    useEffect(() => {
        document.title = `Editar calendari - Aleta`;

        const tabFromPath = getTabFromPath();
        setActiveTab(tabFromPath);

        const handlePopState = () => {
            setActiveTab(getTabFromPath());
        };

        window.addEventListener('popstate', handlePopState);

        socket.emit('.request_calendar');

        const onCalendarEvents = info => {
            setCalendarEvents(info);
            setCalendarError(null);
        };
        const onEventCategoriesChanged = info => {
            setCalendarEvents(previous => ({ ...previous, event_categories: info.event_categories }));
        };
        socket.on('.calendar_events', onCalendarEvents);
        socket.on('.event_categories_changed', onEventCategoriesChanged);

        const onDeletedEvent = () => socket.emit('.request_calendar')
        const onRestoredEvent = () => socket.emit('.request_calendar')
        socket.on('.deleted_event', onDeletedEvent)
        socket.on('.restored_event', onRestoredEvent)

        const onEventsToBeDeleted = events => setEventsToBeDeleted(events.map(event => event.id))
        const onTargetsAddedToAllFutureEvents = () => window.location.reload()
        socket.on('.events_to_be_deleted', onEventsToBeDeleted)
        socket.on('.targets_added_to_all_future_events', onTargetsAddedToAllFutureEvents)

        const onShowCalendarTutorial = (data) => {
            setCalendarError(data.message)
        }
        socket.on('.show_calendar_tutorial', onShowCalendarTutorial)

        return () => {
            window.removeEventListener('popstate', handlePopState);
            socket.off('.calendar_events', onCalendarEvents);
            socket.off('.event_categories_changed', onEventCategoriesChanged);
            socket.off('.deleted_event', onDeletedEvent);
            socket.off('.restored_event', onRestoredEvent);
            socket.off('.events_to_be_deleted', onEventsToBeDeleted);
            socket.off('.targets_added_to_all_future_events', onTargetsAddedToAllFutureEvents);
            socket.off('.show_calendar_tutorial', onShowCalendarTutorial);
        }
    }, []);

    const futureEvents = calendarEvents.events
        .filter(event => applyTimeZone(event["data-esperada-inici"]) > moment())
        .sort((a, b) => applyTimeZone(a["data-esperada-inici"]) > applyTimeZone(b["data-esperada-inici"]) ? 1 : -1)

    const pastEvents = calendarEvents.events
        .filter(event => applyTimeZone(event["data-esperada-inici"]) < moment())
        .sort((a, b) => applyTimeZone(a["data-esperada-inici"]) > applyTimeZone(b["data-esperada-inici"]) ? 1 : -1)

    const orderedEvents = [...pastEvents, ...futureEvents];

    const deleteEventsToBeDeleted = selectedEventIds => {
        selectedEventIds.forEach(id => socket.emit('.delete_event', id))
    }

    const renderCalendarisTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 50 }}>
            {calendarEvents?.calendar_ids?.length === 0 ? (
                <div style={{ marginTop: 20 }}>
                    <p><em>No hi ha cap calendari configurat. Afegeix-ne un des de la pestanya "Afegir".</em></p>
                    <div style={{ height: 100 }} />
                </div>
            ) : null}

            {calendarEvents.calendar_ids && calendarEvents.calendar_ids.length > 0 ? (
                <>
                    <h2>Calendaris de Google</h2>
                    <p>Per canviar els esdeveniments, modifica els calendaris des de l'aplicació <a href='https://calendar.google.com/calendar/'>Google Calendar</a>.</p>
                </>
            ) : null}

            <CalendarsEditor mode="edit" eventCategories={calendarEvents.event_categories} />

            {calendarEvents.calendar_ids && calendarEvents.calendar_ids.length > 0 ? (
                <>
                    {calendarEvents.calendar_ids.map(calendar_id => (
                        <div
                            key={calendar_id}
                            style={{
                                borderStyle: 'solid',
                                borderWidth: 1,
                                borderColor: '#e5e7eb',
                                padding: 16,
                                margin: '8px 0',
                                borderRadius: 10,
                                background: '#fff',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.05)'
                            }}
                        >
                            <iframe src={`https://calendar.google.com/calendar/embed?src=${calendar_id}&ctz=Europe%2FMadrid&mode=MONTH&hl=ca`} width="100%" height="400" frameBorder='0' scrolling="no"></iframe>
                        </div>
                    ))}
                </>
            ) : null}
        </div>
    );

    const renderAfegirTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 50 }}>
            <CalendarsEditor mode="add" eventCategories={calendarEvents.event_categories} />
        </div>
    );

    const renderCompartirTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 50 }}>
            <h2>Compartir calendari</h2>
            <p>Comparteix el calendari amb els membres de la colla perquè el puguin afegir al seu Google Calendar.</p>
            <ShareCalendar />
        </div>
    );

    const renderEsdevenimentsTab = () => (
        <div style={{ paddingBottom: 50 }}>
            <h2>Esdeveniments extrets del calendari</h2>

            {calendarEvents?.calendar_ids?.length === 0 ? (
                <div style={{ marginTop: 20 }}>
                    <p><em>No hi ha cap calendari configurat. Afegeix un calendari a la pestanya "Afegir".</em></p>
                </div>
            ) : null}

            {calendarEvents.calendar_ids ? (
                <CalendarEventsInfiniteTable
                    events={orderedEvents}
                    eventCategories={calendarEvents.event_categories}
                    socket={socket}
                    eventsToBeDeleted={eventsToBeDeleted}
                    onDeleteEventsToBeDeleted={deleteEventsToBeDeleted}
                />
            ) : null}
        </div>
    );

    const renderEsborratsTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 50 }}>
            <h2>Esdeveniments esborrats</h2>
            <p>Gestiona els esdeveniments que han estat esborrats.</p>
            <DeletedEvents socket={socket} autoShow={true} />
        </div>
    );

    const renderTabContent = () => {
        if (activeTab === 'calendaris') {
            return renderCalendarisTab();
        }

        if (activeTab === 'afegir') {
            return renderAfegirTab();
        }

        if (activeTab === 'compartir') {
            return renderCompartirTab();
        }

        if (activeTab === 'esdeveniments') {
            return renderEsdevenimentsTab();
        }

        if (activeTab === 'categories') {
            return <EventCategoriesEditor
                eventCategories={calendarEvents.event_categories}
                onCategoriesChanged={categories => setCalendarEvents(previous => ({ ...previous, event_categories: categories }))}
            />;
        }

        if (activeTab === 'esborrats') {
            return renderEsborratsTab();
        }

        return null;
    };

    return (
        <>
            <NoSignal socket={socket} />
        
            <div
                style={{
                    padding: 20,
                    width: '90%',
                    boxSizing: 'border-box',
                    margin: '0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                }}
            >
                <UserInfo {...props} />

                <HeaderTabs {...props} />

                <Pressable style={{ backgroundColor: '#eee' }} className="boto-back" href='/'>
                    ← Tornar a la pàgina principal
                </Pressable>

                <h1>Gestió del calendari</h1>

                <div className="calendar-layout">
                    <nav className="calendar-navigation" aria-label="Seccions del calendari">
                        {calendarTabs.map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                className={`calendar-tab-button ${activeTab === tab.id ? 'active' : ''}`}
                                aria-current={activeTab === tab.id ? 'page' : undefined}
                                onClick={() => changeTab(tab.id)}
                            >
                                <i className={`fas ${tab.icon}`} aria-hidden="true"></i> {tab.label}
                            </button>
                        ))}
                    </nav>

                    <div className="calendar-content">
                        {calendarError ? (
                            <div style={{
                                backgroundColor: '#ffebee',
                                border: '1px solid #f44336',
                                borderRadius: 5,
                                padding: 20,
                                margin: '20px 0',
                                color: '#c62828'
                            }}>
                                <h3 style={{ margin: '0 0 10px 0', color: '#c62828' }}>Error del calendari</h3>
                                <p style={{ margin: 0 }}>{calendarError}</p>
                                <button
                                    onClick={() => setCalendarError(null)}
                                    style={{
                                        marginTop: 10,
                                        padding: '5px 10px',
                                        backgroundColor: '#f44336',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: 3,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Tancar
                                </button>
                            </div>
                        ) : null}

                        {renderTabContent()}
                    </div>
                </div>

                <div style={{ height: '50px' }}></div>
            </div>
        </>
    )
}

export default CalendarApp;
